/**
 * This component is responsible for:
 * 1. Rendering calendar component with days
 * 2. Fetching available appointments based on time frame of 60 from current date
 * 3. Filtering appointments based on selected appointment type
 * 4. Setting first available day based on filtered appointments
 * 5. Setting timezone abbreviation based on available appointments
 * 6. Navigating to request appointment if no availability
 * 7. Handling calendar date changes
 * 8. Handling today button click
 */
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useReactiveVar, useSuspenseQuery } from '@apollo/client'
import TodayRoundedIcon from '@mui/icons-material/TodayRounded'
import { Typography } from '@mui/material'
import Button from '@mui/material/Button'
import * as Sentry from '@sentry/browser'
import {
  add,
  addDays,
  differenceInDays,
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  formatISO,
  intlFormat,
  isSameDay,
  parse,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { formatInTimeZone, toZonedTime } from 'date-fns-tz'
import PropTypes from 'prop-types'
import { Analytics } from 'shared-components'
import { Divider } from 'shared-components'

import {
  appointmentsAvailable,
  locationData,
  store,
  timezoneAbbr,
} from '../../cache'
import i18next from '../../i18n'
import { APPOINTMENT_AVAILABILITY } from '../../queries/appointmentAvailability'
import { getAvailability } from '../../utils/getAvailability'
import { newestToOldest } from '../../utils/newestToOldest'
import { toISOStringDate } from '../../utils/toISOStringDate'
import { Day } from '../Day'
import { FirstAvailable } from '../FirstAvailable'
import { NextMonth } from './NextMonth'
import { PreviousMonth } from './PreviousMonth'

import './style.css'

export const Calendar = ({
  onDaySelected,
  navigate,
  selectedDay,
  selectedAppointmentType,
}) => {
  // Build calendar state based on today
  const today = new Date()
  const [currentDate, setCurrentDate] = useState(today)
  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)
  const weeks = eachWeekOfInterval({ start: monthStart, end: monthEnd })

  // Setup translation and localization
  const { t } = useTranslation()
  const langCode = i18next.language

  // Get the localized month
  const currentMonthYear = intlFormat(
    currentDate,
    {
      year: 'numeric',
      month: 'long',
    },
    { locale: langCode }
  )

  // Get the localized weekdays
  const localizedWeekdays = () => {
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 })
    const weekdays = Array.from({ length: 7 }).map((_, i) => {
      return intlFormat(
        addDays(weekStart, i),
        { weekday: 'short' },
        { locale: langCode }
      )
    })
    return weekdays
  }
  const weekdays = localizedWeekdays()

  // Setup appointment query variables
  const [startDate, setStartDate] = useState(currentDate)
  const endDate = add(startDate, { days: 60 })

  // Get locationId for the query and partnerId for analytics
  const { locationId, partnerId } = useReactiveVar(store)

  // State for available appointments since we are merging appointments when the user navigates beyond 60 days
  const [availableAppointments, setAvailableAppointments] = useState([])

  // State when the query has completed this is not available when using SuspenseQuery we use it we need to navigate to request appointment
  const [queryCompleted, setQueryCompleted] = useState(false)

  // State for when the first available day is found
  const [firstAvailableDay, setFirstAvailableDay] = useState(null)

  // Filter appointments by selected appointment type
  const filteredAppointments = useMemo(() => {
    if (!selectedAppointmentType) return availableAppointments

    return availableAppointments.filter(
      appointment =>
        appointment?.appointmentType?.id === selectedAppointmentType.id
    )
  }, [availableAppointments, selectedAppointmentType])

  // Fetch available appointments based on locationId, startDate, and endDate
  const { data } = useSuspenseQuery(APPOINTMENT_AVAILABILITY, {
    variables: {
      id: locationId,
      startDateUtc: toISOStringDate(startDate),
      endDateUtc: toISOStringDate(endDate),
    },
  })

  // Update start date when we reach end of 90 day period to refetch available appointments for next period
  useEffect(() => {
    if (differenceInDays(endDate, currentDate) < 0) {
      setStartDate(endDate)
    }
  }, [currentDate, endDate])

  // Parse response and merge appointment data into availableAppointments state
  useEffect(() => {
    if (data?.location?.availableAppointments.length > 0) {
      // Set target timezone based on practice location
      const targetTimezone = locationData()?.timezone

      // Map available appointments into an array and reshape
      const appointments = data?.location?.availableAppointments.map(
        appointment => {
          // Convert UTC timestamp to a date object
          const utcDate = parseISO(appointment.start + 'Z') // Append Z for timezone designator

          // Format the appointment data for display
          const date = format(utcDate, 'yyyy-MM-dd')
          const startTime = formatInTimeZone(
            utcDate,
            targetTimezone,
            'HH:mm:ss'
          )
          const timezone = formatInTimeZone(utcDate, targetTimezone, 'zzz')

          // Store the timezone abbreviation globally for use in other components
          timezoneAbbr(timezone)

          // Store formatted appointment data
          return {
            date,
            startTime,
            timezone,
            ...appointment,
          }
        }
      )
      setAvailableAppointments(appointments)
      setQueryCompleted(true)
      appointmentsAvailable(true)
    } else {
      // Indicate the query has completed although there are no available appointments
      Sentry.captureMessage('No available appointments found')
      setQueryCompleted(true)
      appointmentsAvailable(false)
    }
  }, [data])

  // Set first available date of appointments and pre-select the first available date
  useEffect(() => {
    if (filteredAppointments.length > 0) {
      // Sort appointments and get first available day
      const sortedAppointments = filteredAppointments.toSorted(newestToOldest)
      const firstAvailableDay = parse(
        sortedAppointments[0].date,
        'yyyy-MM-dd',
        new Date()
      )

      // Get availability for first available day
      const availability = getAvailability(
        firstAvailableDay,
        filteredAppointments
      )

      // Set the first available day and times
      setFirstAvailableDay({
        date: new Date(firstAvailableDay),
        times: availability.times,
      })
    } else {
      // Reset if no appointments found for selected appointment type
      onDaySelected(null)
      setFirstAvailableDay(null)
    }
  }, [filteredAppointments])

  // Forward to request appointment if no availability
  useEffect(() => {
    if (queryCompleted && availableAppointments.length === 0) {
      navigate('/request-appointment')
    }
  }, [availableAppointments, queryCompleted])

  // Display current month of the selectedDay when using firstAvailable to change the selectedDay
  useEffect(() => {
    if (selectedDay && !isSameDay(selectedDay, currentDate)) {
      setCurrentDate(selectedDay)
    }
  }, [selectedDay])

  /** Updates the calendar's currently displayed date.
   * If `resetSelectedDay` is `true`, it clears the selected day to prevent times from being displayed.
   * If `resetSelectedDay` is `false`, it retains the selected day (e.g., when navigating to today with available times).
   * @param {Date} date - The selected date
   * @param {boolean} [resetSelectedDay=true] - Whether to reset the selected day (default is `true`)
   * @return {void}
   */
  const setCalendarDate = (date, resetSelectedDay = true) => {
    setCurrentDate(date)
    if (resetSelectedDay) {
      onDaySelected(null)
    }
  }

  /**
   * Goes to today's date in the calendar and performs necessary actions.
   * @return {void}
   */
  const goToToday = () => {
    Analytics.todaySelected({
      locationId,
      partnerId,
      dayOfWeek: format(today, 'EEEE').toUpperCase(),
    })
    const todayAvailability = getAvailability(today, filteredAppointments)
    if (todayAvailability.times.length > 0) {
      setCalendarDate(today, false)
      onDaySelected(today, todayAvailability)
    } else {
      setCalendarDate(today)
    }
  }

  return (
    <div className="calendar">
      <div className="calendar-header">
        <Typography
          className="month-year"
          sx={{
            color: 'var(--intelibly-branding-neutral-900)',
            fontWeight: 500,
          }}
          variant="h6"
        >
          {currentMonthYear}
        </Typography>
        <div className="arrow-button-container">
          <PreviousMonth
            currentDate={currentDate}
            today={today}
            setCalendarDate={setCalendarDate}
            monthStart={new Date(monthStart)}
          />
          <NextMonth
            currentDate={currentDate}
            setCalendarDate={setCalendarDate}
          />
        </div>
      </div>
      <Divider variant="secondary" />
      <Button
        aria-label="today"
        color="primary"
        fullWidth
        onClick={() => goToToday()}
        startIcon={<TodayRoundedIcon />}
        variant="text"
      >
        {t('Today')}
      </Button>
      <div className="date-picker">
        <ul className="weekdays">
          {weekdays.map(day => (
            <li key={day}>
              <Typography variant="body2" className="week-day" key={day}>
                {day}
              </Typography>
            </li>
          ))}
        </ul>
        <ol className="days-grid">
          {weeks.map(startOfWeek =>
            eachDayOfInterval({
              start: startOfWeek,
              end: endOfWeek(startOfWeek),
            }).map(day => (
              <li key={formatISO(day, { representation: 'date' })}>
                <Day
                  day={new Date(day)}
                  availability={getAvailability(day, filteredAppointments)}
                  onSelectDay={onDaySelected}
                  selectedDay={selectedDay}
                />
              </li>
            ))
          )}
        </ol>
      </div>
      {!selectedDay && firstAvailableDay && (
        <FirstAvailable
          firstAvailableDay={firstAvailableDay}
          onDaySelected={onDaySelected}
          selectedDay={selectedDay}
        />
      )}
    </div>
  )
}

Calendar.propTypes = {
  onDaySelected: PropTypes.func,
  selectedDay: PropTypes.instanceOf(Date),
  navigate: PropTypes.func,
  selectedAppointmentType: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
}
